/**
 * BhriguTrimsamshaInfoTable Component
 * Displays D30 Bhrigu Trimsamsha system for spiritual qualities and trials
 * 30 divisions: 10 YAMA + 10 NIYAMA + 10 BHAKTI = 30 spiritual qualities
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateBhriguTrimsamshas,
  BhriguTrimsamshaData,
  BhriguTrimsamshaAttributes,
  SVAMI_DESCRIPTIONS,
  getSvamiType,
  getSpiritualCategory
} from '../data/BhriguTrimsamshaTableData';
import {
  getCategoryColor
} from '../../../../../config/bhriguTrimsamshaColors';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface BhriguTrimsamshaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group divisions by Svami number for display
 */
interface SvamiRow {
  svamiNumber: number;
  svamiType: any;
  category: any;
  planets: BhriguTrimsamshaAttributes[];
}

/**
 * Create all 30 Svami rows with planets grouped
 */
function groupBySvamiNumber(divisions: BhriguTrimsamshaAttributes[]): SvamiRow[] {
  const rows: SvamiRow[] = [];
  const planetsBySvami: Record<number, BhriguTrimsamshaAttributes[]> = {};

  // Initialize all 30 Svamis
  for (let i = 1; i <= 30; i++) {
    planetsBySvami[i] = [];
  }

  // Group planets by Svami number
  divisions.forEach(div => {
    planetsBySvami[div.svamiNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 30; i++) {
    const planetsInSvami = planetsBySvami[i];
    const svamiType = getSvamiType(i);
    const category = getSpiritualCategory(i);

    rows.push({
      svamiNumber: i,
      svamiType,
      category,
      planets: planetsInSvami
    });
  }

  return rows;
}

export const BhriguTrimsamshaInfoTable: React.FC<BhriguTrimsamshaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Bhrigu Trimsamsha divisions
  const trimsamshaData: BhriguTrimsamshaData = calculateBhriguTrimsamshas(apiResponse);

  if (!trimsamshaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Bhrigu Trimsamsha divisions</Text>
      </Box>
    );
  }

  // Group all divisions by Svami number (1-30)
  const svamiRows = groupBySvamiNumber(trimsamshaData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Bhrigu Trimsamsha Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>SVAMI</Th>
              <Th {...COLUMN_STYLES.small}>Category</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Spiritual Quality</Th>
            </Tr>
          </Thead>
          <Tbody>
            {svamiRows.map((row) => {
              const svamiInfo = SVAMI_DESCRIPTIONS[row.svamiType as keyof typeof SVAMI_DESCRIPTIONS];
              const categoryColor = getCategoryColor(row.category);

              return (
                <Tr key={row.svamiNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.svamiNumber}
                    </Text>
                  </Td>

                  {/* SVAMI with color */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={categoryColor}
                    >
                      {svamiInfo.transliteration}
                    </Text>
                  </Td>

                  {/* Category */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text fontSize="xs" fontWeight="normal">
                      {row.category}
                    </Text>
                  </Td>

                  {/* Spiritual Quality */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {svamiInfo.quality}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
